<template>
    <kt-modal
        :visible="recordVisible"
        :title="recordTitle"
        @dismiss="onDismiss()"
        size="modal-lg"
    >
        <template #body>
            <assignment-form ref="assignmentFormRef" v-model="record"></assignment-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex justify-content-between w-100">
                <div>
                    <button 
                        v-if="canStartClock"
                        @click="startClock()" 
                        class="btn btn-success"
                        title="Start a clock for this task"
                    >
                        <Clock :size="16" class="me-1" />Start Clock
                    </button>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary me-2">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger me-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import AssignmentForm from "@/components/Assignments/AssignmentForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save, Clock } from 'lucide-vue-next';
import { TimeModel } from "@/models/TaskModel";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Task Assignment");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    workAssignment: {
        type: String,
        default: ''
    },
    completeByDate: {
        type: String,
        default: ''
    },
    isNew: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const unwatch: Array<Function> = [];

const newRecord = (): TimeModel => {
    const task = new TimeModel();
    task.Type = 'assignment';
    
    // Pre-populate with requestId if provided
    if (props.requestId) {
        task.RequestId = props.requestId;
    }
    
    // Pre-populate with clientId if provided
    if (props.clientId) {
        task.CustomerNumber = props.clientId;
    }
    
    // Pre-populate with techId if provided
    if (props.techId) {
        task.AssignedTo = props.techId;
    }
    
    // Pre-populate with workAssignment if provided
    if (props.workAssignment) {
        task.WorkAssignment = props.workAssignment;
    }
    
    // Pre-populate with completeByDate if provided (from calendar)
    if (props.completeByDate) {
        task.CompleteByDate = props.completeByDate;
    }
    
    return task;
};

const record = ref<TimeModel>(newRecord());
const assignmentFormRef = ref<any>(null);

// Computed property to check if Start Clock button should be shown
const canStartClock = computed(() => {
    return record.value.Id && // Has an ID (saved)
           !record.value.Clock && // No clock assigned
           !record.value.ActualWork && // No work logged
           !record.value.ActualTime; // No time logged
});

// Start clock function
const startClock = () => {
    if (!record.value.Id) return;
    Emitter.emit('new:clock', { taskId: record.value.Id });
    emit('close');
};

const saveRecord = async () => {
    // Ensure Type is set to assignment
    record.value.Type = 'assignment';

    if (assignmentFormRef.value && !assignmentFormRef.value.validate()) {
        return;
    }
    
    try {
        let data = Helper.prep(record.value);
        console.debug("save assignment", data);
        
        data = await ApiService.saveTime(data);
        
        // Check if we're in a route-based context or global modal
        if (route.name && route.name.toString().includes('Assignment')) {
            Emitter.emit("redirect", "/time/edit/" + data.Id);
        } else {
            // Global modal - close after save
            Alert.notify({
                text: "Task assignment saved successfully!",
                type: "success"
            });
            Emitter.emit('assignment:saved', data);
            emit('close');
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify("Deletion is currently disabled.");
};

const onDismiss = () => {
    // Check if we're in a route-based context or global modal
    if (route.name && route.name.toString().includes('Assignment')) {
        router.back();
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const load = async () => {
    // Check prop first, then fall back to route params
    const id = props.id || route.params.id;

    if (id && id !== "new" && !props.isNew) {
        const data = await ApiService.loadTime(id as string);
        
        /*
        if (data.Type !== 'assignment') {
            Alert.notify("This task is not an assignment.");
            Emitter.emit("redirect", "/time/edit/" + id);
            return;
        }
        */
        
        recordTitle.value = "Edit Task Assignment";
        record.value = data;
    } else {
        recordTitle.value = "New Task Assignment";
        record.value = newRecord();
    }
};

// Watch for prop id changes
unwatch.push(watch(() => props.id, () => {
    load();
}, { immediate: true }));

// Watch for route changes (fallback for route-based usage)
unwatch.push(watch(() => route.params.id, () => {
    if (!props.id) {
        load();
    }
}, { immediate: true }));

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
